import { DateTools } from '@treezor/design-system';

import { createHttpHeader } from '@/utils/network/createHTTPHeaders';
import { get } from '@/services/FetchServices';

import { decryptTreezorResponse } from '@/services/encryption/decryptTreezorResponse';

import {
  IKycBaasDocument,
  IKycConnectDocument,
  IKycDocument,
  IKycDocumentPreview,
  IPostKycDocumentResponse,
} from '@/features/kyc/store/models/kyc.model';

import { ICursorElements } from '@/store/types/search.types';

import { IUser } from '@/features/user/store/user.model';

import { KycTools } from '@/utils/tools/KycTools';

import { getStaticDashboardConfig } from '@/hooks/useDashboardConfig';

import { fetchQuery } from '@/utils/queryClient';
import { fetchMethods } from '@/types/FetchService.types';

export class KYCService {
  static queryKey = 'KYC';
  static submitKycReview = async (userId: number): Promise<IUser> => {
    try {
      const config = getStaticDashboardConfig();

      const [user] = await get<IUser>({
        url: `${config.api.baseUrl}/v1/users/${userId}/Kycreview`,
        method: fetchMethods.PUT,
      });
      return user;
    } catch (err) {
      return Promise.reject(err);
    }
  };
  static findUserKycDocuments = async (user: IUser): Promise<{ documents: IKycDocument[] }> => {
    try {
      if (!user) return Promise.reject('user is not valid');

      const connectDocumentsPart = await KYCService.findUsersKycConnectDocuments(user.userId);
      const baasDocumentsPart = await KYCService.findUsersKycBaasDocuments(user.userId);
      return { documents: KycTools.getComputedKycDocuments(connectDocumentsPart, baasDocumentsPart) };
    } catch (err) {
      return Promise.reject(err);
    }
  };
  static findUsersKycConnectDocuments = async (userId: number): Promise<IKycConnectDocument[]> => {
    try {
      const config = getStaticDashboardConfig();

      const documents = await get<IKycConnectDocument>(
        { url: `${config.api.baseUrl}/core-connect/users/${userId}/kyc/document` },
        { shouldSkipEncryption: true },
      );
      return documents ?? [];
    } catch (err) {
      return Promise.reject(err);
    }
  };
  static findUsersKycBaasDocuments = async (userId: number): Promise<IKycBaasDocument[]> => {
    try {
      const config = getStaticDashboardConfig();

      const response = await fetchQuery(
        [this.queryKey, { method: fetchMethods.GET, user: userId }],
        `${config.api.baseUrl}/v1/documents?userId=${userId}`,
        {
          headers: createHttpHeader(),
        },
      );

      const documents = await decryptTreezorResponse<IKycBaasDocument>(response, { shouldSkipEncryption: true });
      return documents ?? [];
    } catch (err) {
      return Promise.reject(err);
    }
  };
  static deleteUserKycConnectDocument = async (documentId: string): Promise<string> => {
    try {
      const config = getStaticDashboardConfig();

      const response = await fetchQuery(
        [this.queryKey, { method: fetchMethods.DELETE, id: documentId }],
        `${config.api.baseUrl}/core-connect/kyc/documents/${documentId}`,
        {
          headers: createHttpHeader(),
        },
      );

      await decryptTreezorResponse<void>(response, { shouldSkipEncryption: true });
      return documentId;
    } catch (err) {
      return Promise.reject(err);
    }
  };
  static deleteUserKycBaasDocument = async (documentId: string): Promise<IKycDocument> => {
    try {
      const config = getStaticDashboardConfig();

      const response = await fetchQuery(
        [this.queryKey, { method: fetchMethods.DELETE, id: documentId }],
        `${config.api.baseUrl}/v1/documents/${documentId}`,
        {
          headers: createHttpHeader(),
        },
      );

      const [document] = await decryptTreezorResponse<IKycBaasDocument>(response, { shouldSkipEncryption: true });
      return KycTools.convertKycBaasDocumentToKycDocument(document);
    } catch (err) {
      return Promise.reject(err);
    }
  };
  static submitConnectKycReview = async (userId: number): Promise<void> => {
    try {
      const config = getStaticDashboardConfig();

      const response = await fetchQuery(
        [this.queryKey, { method: fetchMethods.GET, user: userId }],
        `${config.api.baseUrl}/core-connect/users/${userId}/kyc-review`,
        {
          headers: createHttpHeader(),
        },
      );

      await decryptTreezorResponse(response, { shouldSkipEncryption: true });
    } catch (err) {
      return Promise.reject(err);
    }
  };
  static addUserKycDocument = async (
    documentType: number,
    userId: number,
    file: File,
    batchIndex?: string,
  ): Promise<IKycDocument> => {
    try {
      const config = getStaticDashboardConfig();

      const queryKey = batchIndex ? `${this.queryKey}-${batchIndex}` : this.queryKey;

      const documentResponse = await fetchQuery(
        [queryKey, { method: fetchMethods.POST, user: userId }],
        `${config.api.baseUrl}/core-connect/users/${userId}/kyc/document`,
        {
          headers: createHttpHeader(),
          body: JSON.stringify({
            documentType,
            metadata: {},
          }),
        },
      );
      const s3Request = await decryptTreezorResponse<IPostKycDocumentResponse>(documentResponse, {
        shouldSkipEncryption: true,
        isSingle: true,
      });
      s3Request.formFields['Content-Type'] = file.type;
      const formData = new FormData();
      for (const [key, value] of Object.entries(s3Request.formFields)) {
        formData.append(key, value as string);
      }
      formData.append('file', file);
      const responseUpload = await fetchQuery([queryKey, { method: fetchMethods.POST }], s3Request.form.action, {
        method: s3Request.form.method,
        body: formData,
      });
      if (!responseUpload.ok) return Promise.reject(await responseUpload.json());
      return {
        documentId: s3Request.documentId,
        documentType,
        status: 'PENDING',
        isAgent: true,
        userId,
        createdAt: DateTools.now().toISOString(),
        updatedAt: DateTools.now().toISOString(),
      } as IKycDocument;
    } catch (err) {
      return Promise.reject(err);
    }
  };
  static getPreviewKycDocument = async (documentId: string): Promise<IKycDocumentPreview> => {
    try {
      const config = getStaticDashboardConfig();
      const url = `${config.api.baseUrl}/core-connect/kyc/documents/${documentId}/preview`;

      const response = await fetchQuery([this.queryKey, { method: fetchMethods.GET, id: documentId }], url, {
        headers: createHttpHeader(),
      });

      return await decryptTreezorResponse<IKycDocumentPreview>(response, {
        shouldSkipEncryption: true,
        isSingle: true,
      });
    } catch (err) {
      return Promise.reject(err);
    }
  };

  static getBaasPreviewKycDocument = async (documentId: string): Promise<IKycDocumentPreview> => {
    try {
      const config = getStaticDashboardConfig();
      const url = `${config.api.baseUrl}/v1/documents/${documentId}/download`;

      const response = await fetchQuery([this.queryKey, { method: fetchMethods.GET, id: documentId }], url, {
        headers: createHttpHeader(),
      });

      return await decryptTreezorResponse<IKycDocumentPreview>(response, {
        shouldSkipEncryption: true,
        isSingle: true,
      });
    } catch (err) {
      return Promise.reject(err);
    }
  };

  static editKycStatusDocument = async (documentId: string, status: number, comment: string): Promise<IKycDocument> => {
    try {
      const config = getStaticDashboardConfig();

      const response = await fetchQuery(
        [this.queryKey, { method: fetchMethods.PUT, id: documentId }],
        `${config.api.baseUrl}/core-connect/kyc/documents/${documentId}/status`,
        {
          headers: createHttpHeader(),
          body: JSON.stringify({
            status,
            comment,
          }),
        },
      );
      const connectDocument = await decryptTreezorResponse<IKycConnectDocument>(response, {
        shouldSkipEncryption: true,
        isSingle: true,
      });
      return KycTools.convertKycConnectDocumentToKycDocument(connectDocument);
    } catch (err) {
      return Promise.reject(err);
    }
  };
  static getNeedReviewKycDocument = async (cursor?: string): Promise<ICursorElements<IKycDocument>> => {
    try {
      const config = getStaticDashboardConfig();
      const cursorInfo = cursor ? `?cursor=${cursor}` : '';
      const url = `${config.api.baseUrl}/core-connect/kyc/documents/need-review${cursorInfo}`;
      const queryKey = `${this.queryKey}-need-review${cursorInfo}`;

      const response = await fetchQuery([queryKey, { method: fetchMethods.GET, action: 'review' }], url, {
        headers: createHttpHeader(),
        staleTime: 60000,
      });

      const connectDocuments: ICursorElements<IKycConnectDocument> = await decryptTreezorResponse(response, {
        shouldSkipEncryption: true,
        hasCursor: true,
      });

      return {
        ...connectDocuments,
        elements: (connectDocuments.elements as IKycConnectDocument[]).map((connectDocument) =>
          KycTools.convertKycConnectDocumentToKycDocument(connectDocument),
        ),
      };
    } catch (err) {
      return Promise.reject(err);
    }
  };
}
